import axios from 'axios';
import { FeatureFlags } from '@/config/feature-flags';

const API_URL = `${import.meta.env.VITE_API_URL}/api/v1/core/feature-flags`;

export const getFeatureFlags = async (accessToken?: string): Promise<FeatureFlags> => {
  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    // Only add Authorization header if token is provided
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    const response = await axios.get(API_URL, { headers });

    // Validate response data
    if (!response.data || typeof response.data !== 'object') {
      console.warn('Feature flags API returned invalid data:', response.data);
      return {} as FeatureFlags;
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching feature flags:', error);
    if (axios.isAxiosError(error)) {
      console.error('API Error Details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: API_URL,
      });
    }
    throw error;
  }
};

export const updateFeatureFlag = async (
  accessToken: string,
  flagName: string,
  value: boolean
): Promise<FeatureFlags> => {
  try {
    const payload = { [flagName]: value };
    console.log('Updating feature flag:', { flagName, value, payload });

    const response = await axios.patch(API_URL, payload, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    // Validate response data
    if (!response.data || typeof response.data !== 'object') {
      console.warn('Feature flags update API returned invalid data:', response.data);
      // Return the updated flag even if API doesn't return it
      return { [flagName]: value } as FeatureFlags;
    }

    // Log the response for debugging
    console.log('Feature flags update API response:', response.data);

    // Ensure the updated flag is in the response
    if (!Object.prototype.hasOwnProperty.call(response.data, flagName)) {
      console.warn(`API response missing updated flag '${flagName}', adding it to response`);
      response.data[flagName] = value;
    }

    return response.data;
  } catch (error) {
    console.error('Error updating feature flag:', error);
    if (axios.isAxiosError(error)) {
      console.error('API Error Details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        url: API_URL,
        payload: { [flagName]: value },
      });
    }
    throw error;
  }
};

export const getUserFeatureFlags = async (
  accessToken: string,
  userProfileId: string
): Promise<Record<string, boolean>> => {
  try {
    const response = await axios.get(`${API_URL}/user/${userProfileId}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.data || typeof response.data !== 'object') {
      return {};
    }

    return response.data;
  } catch (error) {
    console.error('Error fetching user feature flags:', error);
    throw error;
  }
};

export const updateUserFeatureFlag = async (
  accessToken: string,
  userProfileId: string,
  flagName: string,
  value: boolean
): Promise<Record<string, boolean>> => {
  try {
    const payload = { [flagName]: value };
    const response = await axios.patch(`${API_URL}/user/${userProfileId}`, payload, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.data || typeof response.data !== 'object') {
      return { [flagName]: value };
    }

    return response.data;
  } catch (error) {
    console.error('Error updating user feature flag:', error);
    throw error;
  }
};

export const updateFeatureFlagSetting = async (
  accessToken: string,
  settingName: string,
  value: string
): Promise<Record<string, unknown>> => {
  try {
    const payload = { [settingName]: value };
    const response = await axios.patch(API_URL, payload, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.data || typeof response.data !== 'object') {
      return { [settingName]: value };
    }

    return response.data as Record<string, unknown>;
  } catch (error) {
    console.error('Error updating feature flag setting:', error);
    throw error;
  }
};

/**
 * PATCH /core/feature-flags with an arbitrary JSON-typed setting. Used for
 * structured settings such as `rate_limit_overrides` where the backend expects
 * an object/array value rather than a string scalar.
 */
export const updateFeatureFlagJsonSetting = async (
  accessToken: string,
  settingName: string,
  value: unknown
): Promise<Record<string, unknown>> => {
  try {
    const payload = { [settingName]: value };
    const response = await axios.patch(API_URL, payload, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.data || typeof response.data !== 'object') {
      return { [settingName]: value } as Record<string, unknown>;
    }

    return response.data as Record<string, unknown>;
  } catch (error) {
    console.error('Error updating feature flag JSON setting:', error);
    throw error;
  }
};
